import type { Lang } from '@/lib/i18n';

export type ProductSectionData = {
  id: string;
  kicker: string;
  title: string;
  sub: string;
  items: ReadonlyArray<readonly [string, string]>;
};

export type ProductDict = {
  page_title: string;
  page_desc: string;
  nav_home: string; nav_product: string;
  nav_signin: string; nav_signup: string;
  hero_kicker: string;
  hero_title: string;
  hero_sub: string;
  cta_signup: string;
  sections: ProductSectionData[];
  cta_title: string; cta_sub: string; cta_btn: string;
  foot_tag: string;
  foot_product: string; foot_links_p: ReadonlyArray<readonly [string, string]>;
  foot_company: string; foot_links_c: ReadonlyArray<readonly [string, string]>;
  foot_help: string;    foot_links_h: ReadonlyArray<readonly [string, string]>;
  foot_legal: string;
  foot_copy: string;
};

export const I18N: Record<Lang, ProductDict> = {
  en: {
    page_title: 'Product — Tax Invoice, VAT, e-Tax & PromptPay | Invosly',
    page_desc:  'Compliance-first invoicing for Thai businesses with full VAT, WHT, e-Tax and PromptPay support.',
    nav_home: 'Home', nav_product: 'Product', nav_signin: 'Sign in', nav_signup: 'Start free',
    hero_kicker: 'PRODUCT',
    hero_title: 'Invosly: Smart & Simple — Smart invoicing built for Thai businesses',
    hero_sub:   'Issue a Tax Invoice in 60 seconds. Built-in VAT calculation, e-Tax compliant, print and one-click share.',
    cta_signup: 'Start free →',
    sections: [
      { id: 'compliance',  kicker: 'COMPLIANCE',
        title: 'Tax compliance — built in, not bolted on',
        sub:   'Every Tax Invoice you issue lands all 13 mandatory fields under RD Section 86, with VAT 7%, WHT certificates, and PDF/A archival ready out of the box.',
        items: [
          ['Bilingual title', 'Every PDF carries the dual-language Tax Invoice header in Thai and English with the original/copy marker.'],
          ['Section 86 fields', 'Seller Tax ID + branch, buyer Tax ID + branch, sequential invoice number, issue date, full VAT breakdown — all auto-populated.'],
          ['VAT 7% engine',    'Inclusive or exclusive mode per invoice. The 7% rate is configurable per organization (set to 0% for export).'],
          ['WHT certificates', '5 standard rates (1/3/5/10/15%) with auto-generated 50 ทวิ certificates for both 3 (individual) and 53 (corporate) forms.'],
          ['e-Tax pathway',    'Phase 1 produces high-quality PDFs that meet RD\'s e-Tax by Email programme. Digital signature and PKCS#7 are on the Phase 2 roadmap.'],
          ['PDF/A-1b output',  'Long-term archival format — 7-year retention by default to match Thai tax law.'],
        ] },
      { id: 'simplicity',  kicker: 'SIMPLICITY',
        title: '30 seconds from idea to invoice',
        sub:   'No accounting jargon, no setup wizard, no learning curve. Pick a customer, type the amount, click send.',
        items: [
          ['3-step workflow',     'Pick customer → enter line items → click Send. The PDF is rendered server-side and delivered in under 2 seconds.'],
          ['Auto-fill customers', 'Type a Tax ID — we look it up in your customer book and pre-fill name, address, branch, email and Line ID.'],
          ['Mobile-first UI',     'Every screen works on a phone. Issue invoices from a customer\'s shop counter, your taxi, or your kitchen table.'],
          ['Customer QR scan',    'V3.0 industry-first: scan a counterparty\'s Invoice Card QR and their tax info auto-fills your buyer form.'],
          ['Smart defaults',      'Today\'s date, +30 days due, recently used items, your default Invoice Card — pre-selected so the form is mostly done before you start.'],
          ['Save draft, resume',  'Don\'t finish? Auto-saves every 3 seconds. Pick up on a different device.'],
        ] },
      { id: 'workflow',    kicker: 'WORKFLOW',
        title: 'Six document types, three flow modes — one engine',
        sub:   'A complete document automation engine. Quotation, Invoice, Delivery Order, Tax Invoice, Receipt and WHT Certificate chain together with one-click conversion. Three flow modes match the way Thai SMEs, B2B firms and project businesses actually work.',
        items: [
          ['6 document types',         'Quotation / Invoice / Delivery Order / Tax Invoice / Receipt / WHT Certificate — each can stand alone or chain into the next.'],
          ['1-click conversion',       'Quotation → Invoice → Tax Invoice → Receipt without retyping. The chain is enforced server-side; converted source documents lock automatically.'],
          ['Flow mode A (SME)',        'Issue Invoice first, then Tax Invoice once paid. Standard SME pattern — about 60% of Thai businesses.'],
          ['Flow mode B (B2B)',        'Issue the Tax Invoice directly with the goods/services. Standard pattern for B2B trade.'],
          ['Flow mode C (projects)',   'Deposit Invoice for the down payment, then Tax Invoice on completion. Standard pattern for engineering and project work.'],
          ['Per-card numbering',       'Multi-identity? Each Invoice Card carries its own number prefix (e.g. SI 2603 vs IV 2603) — no cross-contamination.'],
        ] },
      { id: 'ai-tools',    kicker: 'AI TAX TOOLS',
        title: 'AI does the boring tax work',
        sub:   'Snap a photo of a 50 ทวิ certificate and Claude Vision extracts every field. Customer book and item library de-duplicate automatically. Multi-copy PDFs in one render.',
        items: [
          ['WHT OCR',               'Upload or photograph a 50 ทวิ certificate. Claude Vision extracts payer, payee, income type, base amount, WHT rate, certificate date — pre-filled into the form for you to confirm.'],
          ['Customer dedupe',       'Customer book auto-merges (name, normalized Tax ID) so issuing twice to the same buyer doesn\'t create duplicate records.'],
          ['Item library',          'Line item descriptions auto-suggest from your history with default prices and units. Type the first letter, see the past three.'],
          ['Multi-copy PDF',        'Original × N + Copy × M in one render. Standard configuration for Thai accounting (one Original kept by buyer, one Copy retained by seller).'],
          ['VAT report (P.P.30)',   'Monthly VAT-30 report with single-click Excel export — your accountant can file directly.'],
          ['WHT report (P.N.D.53)', 'WHT-53 report aggregated from your 50 ทวิ certificates, with the underlying PDFs bundled for tax-office submission.'],
        ] },
      { id: 'security',    kicker: 'SECURITY',
        title: 'Bank-grade security with full PDPA compliance',
        sub:   'Sensitive fields are encrypted at the database level. Audit logs are append-only. Data residency is in Thailand-region AWS.',
        items: [
          ['AES-256 field encryption', 'Tax IDs, bank accounts and PromptPay numbers are individually encrypted using pgcrypto + a key rotated quarterly.'],
          ['PDPA data subject API',    'One-click export of all your data as JSON, and one-click full deletion (with 30-day soft-delete grace period).'],
          ['Append-only audit log',    'Every login, every invoice issued, every status change — recorded immutably. Retained 7 years per Thai tax law.'],
          ['Thailand-region hosting',  'Primary AWS Singapore (closest region with full PDPA acceptance) with Thailand-region disaster recovery.'],
          ['JWT + 2FA',                'Short-lived access tokens with refresh rotation. Two-factor authentication available on Business plan.'],
          ['Annual penetration test',  'Independent security firm runs a full pen-test every 12 months — report available under NDA on request.'],
        ] },
    ],
    cta_title: 'Try it on your real workflow', cta_sub: 'Free tier is generous enough that you may never need to pay. Take it for a spin.',
    cta_btn: 'Sign up free →',
    foot_tag: 'Smart invoicing built for Thai Revenue Department compliance, by a team that ships.',
    foot_product: 'Product', foot_links_p: [['Home','/'],['Product','/product']],
    foot_company: 'Company', foot_links_c: [['About','#'],['Blog','#'],['Contact','#']],
    foot_help:    'Help',    foot_links_h: [['Docs','#'],['FAQ','#'],['Status','#']],
    foot_legal:   'Privacy · Terms',
    foot_copy:    'Invosly Co., Ltd. · All rights reserved',
  },
  th: {
    page_title: 'สินค้า — ใบกำกับภาษี VAT e-Tax และ PromptPay | Invosly',
    page_desc:  'ระบบใบกำกับภาษีสำหรับธุรกิจไทย รองรับ VAT, WHT, e-Tax และ PromptPay เต็มรูปแบบ',
    nav_home: 'หน้าแรก', nav_product: 'สินค้า', nav_signin: 'เข้าสู่ระบบ', nav_signup: 'เริ่มฟรี',
    hero_kicker: 'สินค้า', hero_title: 'Invosly: Smart & Simple — ระบบใบกำกับภาษีอัจฉริยะสำหรับธุรกิจไทย',
    hero_sub: 'ออกใบกำกับภาษีใน 60 วินาที คำนวณ VAT อัตโนมัติ รองรับ e-Tax พิมพ์และแชร์ในคลิกเดียว',
    cta_signup: 'เริ่มฟรี →',
    sections: [
      { id: 'compliance', kicker: 'ความถูกต้อง', title: 'ความถูกต้องทางภาษี — อยู่ในระบบ ไม่ใช่ติดเข้ามา', sub: 'ทุกใบกำกับภาษีครบ 13 ฟิลด์บังคับตามมาตรา 86 พร้อม VAT 7% และ WHT พร้อม PDF/A ไม่ต้องตั้งค่า',
        items: [
          ['หัวภาษาคู่','ทุก PDF มีหัวใบกำกับภาษีไทย-อังกฤษ พร้อมระบุต้นฉบับ/สำเนา'],
          ['ฟิลด์ Section 86','Tax ID ผู้ขาย + สาขา, ผู้ซื้อ + สาขา, เลขใบไม่ซ้ำ, วันที่ออก, รายละเอียด VAT — กรอกอัตโนมัติ'],
          ['VAT 7% engine','โหมด include/exclude ต่อใบ ตั้งอัตราต่อองค์กรได้ (0% สำหรับส่งออก)'],
          ['WHT certificates','5 อัตรา (1/3/5/10/15%) สร้าง 50 ทวิ อัตโนมัติทั้งบุคคลและนิติบุคคล'],
          ['เส้นทาง e-Tax','Phase 1 ออก PDF คุณภาพสูง ตรงโครงการ e-Tax by Email ของกรมฯ ลายเซ็นดิจิทัลในแผน Phase 2'],
          ['PDF/A-1b','รูปแบบเก็บถาวรระยะยาว เก็บ 7 ปีตามกฎหมายภาษีไทย'],
        ] },
      { id: 'simplicity', kicker: 'ใช้ง่าย', title: '30 วินาทีจากความคิดเป็นใบกำกับ', sub: 'ไม่มีศัพท์บัญชี ไม่มี wizard ตั้งค่า ไม่ต้องเรียน เลือกลูกค้า ใส่จำนวน คลิกส่ง',
        items: [
          ['3 ขั้นตอน','เลือกลูกค้า → ใส่รายการ → คลิกส่ง PDF render บน server พร้อมส่งภายใน 2 วินาที'],
          ['Auto-fill ลูกค้า','พิมพ์ Tax ID ระบบจะ lookup ใน customer book และเติมชื่อ ที่อยู่ สาขา อีเมล Line ID'],
          ['Mobile-first','ทุกหน้าใช้บนมือถือได้ ออกใบจากเคาน์เตอร์ลูกค้า รถแท็กซี่ หรือโต๊ะกินข้าว'],
          ['สแกน QR ลูกค้า','V3.0 ครั้งแรกในวงการ สแกน QR นามบัตรของคู่ค้า แล้ว auto-fill ฟอร์มผู้ซื้อทันที'],
          ['ค่าเริ่มต้นฉลาด','วันนี้ +30 วันครบกำหนด รายการล่าสุด นามบัตรเริ่มต้น — เลือกล่วงหน้า ฟอร์มเสร็จก่อนคุณเริ่ม'],
          ['Save draft','ทำไม่จบ? auto-save ทุก 3 วินาที กลับมาทำต่อได้จากเครื่องอื่น'],
        ] },
      { id: 'workflow', kicker: 'ระบบเอกสาร', title: '6 ประเภทเอกสาร 3 รูปแบบธุรกิจ — เครื่องยนต์เดียว', sub: 'ระบบเอกสารครบวงจร ใบเสนอราคา / Invoice / ใบส่งของ / ใบกำกับภาษี / ใบเสร็จ / ใบ 50 ทวิ คลิกเดียวเชื่อมเป็นสาย 3 รูปแบบธุรกิจตรงกับวิธีทำงานจริงของ SME B2B และธุรกิจโครงการ',
        items: [
          ['6 ประเภทเอกสาร','ใบเสนอราคา / Invoice / ใบส่งของ / ใบกำกับภาษี / ใบเสร็จ / ใบ 50 ทวิ — แต่ละแบบยืนเดี่ยวได้ หรือเชื่อมกันก็ได้'],
          ['คลิกเดียวแปลง','ใบเสนอราคา → Invoice → ใบกำกับภาษี → ใบเสร็จ ไม่ต้องพิมพ์ซ้ำ ระบบ enforce ที่ server ใบต้นทางล็อคอัตโนมัติเมื่อแปลง'],
          ['Flow mode A (SME)','ออก Invoice ก่อน ตามด้วยใบกำกับภาษีเมื่อรับชำระ — ~60% ของธุรกิจไทย'],
          ['Flow mode B (B2B)','ออกใบกำกับภาษีเลยพร้อมส่งสินค้า/บริการ — รูปแบบมาตรฐาน B2B'],
          ['Flow mode C (โครงการ)','Invoice มัดจำ ตามด้วยใบกำกับภาษีเมื่อปิดงาน — รูปแบบมาตรฐานวิศวกรรมและโครงการ'],
          ['Numbering ต่อนามบัตร','หลายเอกลักษณ์? นามบัตรแต่ละใบมี prefix เลขใบของตัวเอง (เช่น SI 2603 vs IV 2603) — ไม่ปนกัน'],
        ] },
      { id: 'ai-tools', kicker: 'เครื่องมือ AI', title: 'AI ทำงานภาษีน่าเบื่อแทน', sub: 'ถ่ายรูป 50 ทวิ — Claude Vision อ่านทุกฟิลด์ Customer book และ item library ลบซ้ำเอง PDF หลายสำเนาในการ render เดียว',
        items: [
          ['WHT OCR','อัปโหลดหรือถ่ายรูปใบ 50 ทวิ Claude Vision อ่านผู้จ่าย ผู้รับ ประเภทรายได้ ฐานเงิน อัตรา WHT วันที่ — กรอกล่วงหน้าให้ยืนยัน'],
          ['Dedupe ลูกค้า','Customer book รวมโดย (ชื่อ, Tax ID normalize) — ออกใบให้ลูกค้าเดิม 2 ครั้งจะไม่สร้างเรคคอร์ดซ้ำ'],
          ['Library สินค้า','รายการในไอเทม auto-suggest จากประวัติ พร้อมราคาเริ่มต้นและหน่วย พิมพ์ตัวแรกเห็น 3 ตัวล่าสุด'],
          ['PDF หลายสำเนา','Original × N + Copy × M ในการ render เดียว มาตรฐานบัญชีไทย (1 Original ผู้ซื้อ 1 Copy ผู้ขาย)'],
          ['รายงาน VAT (ภ.พ.30)','รายงาน VAT-30 รายเดือน ส่งออก Excel ได้ในคลิกเดียว นักบัญชีนำส่งได้ทันที'],
          ['รายงาน WHT (ภ.ง.ด.53)','รายงาน WHT-53 รวมจากใบ 50 ทวิ ของคุณ พร้อม PDF แนบไปยื่นกรมสรรพากร'],
        ] },
      { id: 'security', kicker: 'ความปลอดภัย', title: 'ระดับธนาคาร พร้อม PDPA เต็มรูปแบบ', sub: 'ฟิลด์ละเอียดอ่อนเข้ารหัสที่ระดับ DB Audit log แบบ append-only ข้อมูลตั้งใน AWS Singapore',
        items: [
          ['AES-256','Tax ID, บัญชีธนาคาร, PromptPay เข้ารหัสรายฟิลด์ ผ่าน pgcrypto กุญแจหมุนทุกไตรมาส'],
          ['PDPA API','export ทั้งบัญชีเป็น JSON ในคลิกเดียว ลบทั้งบัญชีในคลิกเดียว(soft-delete 30 วัน)'],
          ['Audit append-only','ทุก login ทุกใบที่ออก ทุกเปลี่ยนสถานะ — บันทึกไม่สามารถแก้ไข เก็บ 7 ปี'],
          ['Hosting ในไทย','AWS Singapore (PDPA-friendly) สำรองในไทย'],
          ['JWT + 2FA','Access token อายุสั้น พร้อม refresh rotation 2FA บน Business'],
          ['Pen-test ปีละครั้ง','บริษัทรักษาความปลอดภัยอิสระทดสอบทุก 12 เดือน รายงานขอผ่าน NDA'],
        ] },
    ],
    cta_title: 'ลองใช้งานจริง', cta_sub: 'Free tier ใจกว้างพอจนคุณอาจไม่ต้องจ่ายเงิน ลองดูสิ',
    cta_btn: 'สมัครฟรี →',
    foot_tag: 'ระบบใบกำกับอัจฉริยะ ตรงมาตรฐานสรรพากร โดยทีมที่ส่งของจริง',
    foot_product: 'สินค้า', foot_links_p: [['หน้าแรก','/'],['สินค้า','/product']],
    foot_company: 'บริษัท', foot_links_c: [['เกี่ยวกับ','#'],['Blog','#'],['ติดต่อ','#']],
    foot_help: 'ช่วยเหลือ', foot_links_h: [['Docs','#'],['FAQ','#'],['สถานะ','#']],
    foot_legal: 'นโยบายความเป็นส่วนตัว · ข้อกำหนด',
    foot_copy: 'Invosly Co., Ltd. · สงวนลิขสิทธิ์',
  },
  zh: {
    page_title: '产品介绍 — Tax Invoice、VAT、e-Tax 与 PromptPay | Invosly',
    page_desc:  '完整的泰国发票产品介绍：30 秒开票、合规、6 种单据自动化、AI 税务工具、安全。',
    nav_home: '首页', nav_product: '产品介绍', nav_signin: '登录', nav_signup: '免费开始',
    hero_kicker: '产品',
    hero_title: 'Invosly: Smart & Simple — 专为泰国企业打造的智能开票系统',
    hero_sub:   '60 秒开出 Tax Invoice,内置 VAT 算法,符合 e-Tax 合规要求,支持打印和一键分享。',
    cta_signup: '免费开始 →',
    sections: [
      { id: 'compliance',  kicker: '完全合规',
        title: '合规——内置,而非外挂',
        sub:   '每张你开的 Tax Invoice 都覆盖泰国 RD Section 86 全部 13 个强制字段,VAT 7%、WHT 凭证和 PDF/A 归档全部开箱即用。',
        items: [
          ['泰英双语标题','每张 PDF 都带泰英双语 Tax Invoice 抬头,以及原件/副本标识。'],
          ['Section 86 全字段','卖方 Tax ID + 分支、买方 Tax ID + 分支、不跳号发票号、开票日期、完整 VAT 拆分 — 全部自动生成。'],
          ['VAT 7% 引擎','每张发票可切换含税/不含税。税率按公司可配置(出口业务设 0%)。'],
          ['WHT 凭证','5 档标准税率(1/3/5/10/15%),自动生成 50 ทวิ 凭证(个人 ภ.ง.ด.3 + 公司 ภ.ง.ด.53)。'],
          ['e-Tax 演进','Phase 1 输出高质量 PDF,符合 RD 的 e-Tax by Email 项目。数字签名 + PKCS#7 在 Phase 2 路线图。'],
          ['PDF/A-1b','长期归档格式 — 默认保留 7 年,与泰国税法一致。'],
        ] },
      { id: 'simplicity',  kicker: '简单易用',
        title: '30 秒从想法到一张发票',
        sub:   '没有会计行话、没有设置向导、没有学习曲线。选客户、填金额、发送。',
        items: [
          ['3 步流程','选客户 → 填行项 → 点发送。PDF 服务端渲染,2 秒内送达。'],
          ['客户自动联想','输入 Tax ID,系统在客户库 lookup,自动填充名称、地址、分支、邮箱、Line ID。'],
          ['移动端优先','每个屏幕都能在手机上用。在客户柜台前、出租车上、餐桌边开票。'],
          ['扫客户 QR','V3.0 行业首创:扫描对方 Invoice 名片 QR,买方表单自动填好。'],
          ['智能默认值','今日日期、+30 天到期、最近用过的商品、你的默认名片 — 预选好,表单基本填完才开始。'],
          ['草稿与续编','没填完?每 3 秒自动保存。在另一台设备继续。'],
        ] },
      { id: 'workflow',    kicker: '单据流转',
        title: '6 种单据 3 种业务流程 — 一个引擎',
        sub:   '完整的单据自动化引擎。报价单 / 发票 / 送货单 / 增值税发票 / 收据 / 预扣税凭证 一键转换链路。3 种业务流程对应 SME / B2B / 工程项目的真实工作方式。',
        items: [
          ['6 种单据类型','报价单 / 发票 / 送货单 / 增值税发票 / 收据 / 预扣税凭证 — 每种都可独立创建,或串成链路。'],
          ['一键转换','报价单 → 发票 → 增值税发票 → 收据,无需重复录入。链路在服务端 enforce,转换后源单据自动锁定。'],
          ['Flow A (SME)','先开发票,收款后开增值税发票。SME 标准模式 —— 约占泰国企业 60%。'],
          ['Flow B (B2B)','直接开增值税发票同时交付货物/服务。B2B 贸易标准模式。'],
          ['Flow C (工程)','先开预付款发票,完工后补增值税发票。工程与项目类业务标准模式。'],
          ['名片级编号','多重身份?每张 Invoice 名片有自己的发票号前缀(例如 SI 2603 vs IV 2603)—— 互不交叉。'],
        ] },
      { id: 'ai-tools',    kicker: 'AI 税务工具',
        title: 'AI 替你处理无聊的税务工作',
        sub:   '拍一张 50 ทวิ 凭证 — Claude Vision 抽取每个字段。客户库与商品库自动 dedupe。多副本 PDF 一次渲染输出。',
        items: [
          ['WHT 凭证 OCR','上传或拍照 50 ทวิ 凭证。Claude Vision 抽取付款方、收款方、收入类型、计税基数、WHT 税率、凭证日期 —— 预填进表单等你确认。'],
          ['客户去重','客户库按 (姓名 + 标准化 Tax ID) 自动合并,给同一买方开两张发票不会建两条客户记录。'],
          ['商品库联想','商品行描述按历史自动联想,带默认价格和单位。输入首字母看到最近 3 条。'],
          ['多副本 PDF','Original × N + Copy × M 一次渲染输出。泰国会计标准配置(买方留 1 张 Original,卖方留 1 张 Copy)。'],
          ['VAT 报表 (ภ.พ.30)','月度 VAT-30 报表一键导出 Excel,你的会计可直接报送。'],
          ['WHT 报表 (ภ.ง.ด.53)','WHT-53 报表汇总自你的 50 ทวิ 凭证,附带 PDF 打包供税务局申报。'],
        ] },
      { id: 'security',    kicker: '数据安全',
        title: '银行级安全 + 完整 PDPA 合规',
        sub:   '敏感字段在数据库层级加密。审计日志只追加。数据存放在泰国区 AWS。',
        items: [
          ['AES-256 字段加密','Tax ID、银行账号、PromptPay 号码用 pgcrypto 单独加密,密钥每季度轮换。'],
          ['PDPA 数据主体 API','一键导出全账户为 JSON,一键完全删除(30 天软删除缓冲期)。'],
          ['只追加审计日志','每次登录、每张发票、每次状态变更 — 不可篡改记录。按泰国税法保留 7 年。'],
          ['泰国区域托管','主区 AWS 新加坡(最近的 PDPA 友好区)+ 泰国区灾备。'],
          ['JWT + 2FA','短期 access token + refresh rotation。Business 套餐含两步验证。'],
          ['每年渗透测试','独立安全公司每 12 个月做一次完整渗透测试,报告 NDA 后可申请。'],
        ] },
    ],
    cta_title: '用真实工作流试一下', cta_sub: '免费版足够慷慨,你可能永远不需要付费。来试试看。',
    cta_btn: '免费注册 →',
    foot_tag: '符合泰国税务局合规要求的智能开票系统,由真正交付的团队打造。',
    foot_product: '产品', foot_links_p: [['首页','/'],['产品介绍','/product']],
    foot_company: '公司', foot_links_c: [['关于我们','#'],['博客','#'],['联系我们','#']],
    foot_help:    '帮助', foot_links_h: [['文档','#'],['FAQ','#'],['系统状态','#']],
    foot_legal:   '隐私政策 · 服务条款',
    foot_copy:    'Invosly Co., Ltd. · 保留所有权利',
  },
};
